import { Box, Menu, MenuItem, TextField, TextFieldProps, Typography, Tooltip } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useMenu } from 'common/hooks/useMenu';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { EditableSelect } from 'stores/editor/EditableValue';

type SelectEditorProps<T> = {
  value: EditableSelect<T>;
  getDisplay?: (value: T) => string;
  autoFocus?: boolean;
  nested?: boolean;
  small?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

const SelectEditorCore = <T extends any>(props: SelectEditorProps<T>) => {
  const { value: eValue, autoFocus, getDisplay, nested, small, disabled, tooltip } = props;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [value, setValue] = React.useState(eValue.value ?? '');

  const onChange = e => {
    setValue(e.target.value);
  };
  const hasError = Boolean(eValue.error);
  const errorProps: TextFieldProps = hasError ? { error: true, helperText: eValue.error } : null;

  const [handleOpen, handleClose, menuProps] = useMenu();

  React.useEffect(() => {
    setValue(eValue.value ?? '');
  }, [eValue.key, eValue.value]);

  const smallProps = small
    ? { variant: 'outlined' as const, label: eValue.label }
    : { variant: 'standard' as const };

  const inputField = (
    <TextField
      size={'small'}
      {...smallProps}
      inputRef={inputRef}
      inputProps={{ readOnly: true }}
      value={getDisplay ? getDisplay(value as T) : value}
      onChange={onChange}
      // onBlur={onBlur}
      {...errorProps}
      autoFocus={autoFocus}
      disabled={disabled}
      onKeyDown={ev => {
        console.debug(ev.key);
        if (ev.key === 'Enter' || ev.key === 'Escape') {
          inputRef.current?.blur();
        }
      }}
      sx={{
        p: 1,
      }}
      // onClick={disabled ? undefined : handleOpen}
      onMouseDown={ev => {
        if (disabled) return;
        ev.preventDefault();
        handleOpen(ev as any);
      }}
    ></TextField>
  );

  return (
    <Box display='flex' alignItems='baseline'>
      {!small && (
        <Typography sx={nested ? { color: grey[500] } : { width: '100px', mr: 1 }}>
          {eValue.label}
        </Typography>
      )}
      {tooltip ? (
        <Tooltip
          title={tooltip}
          componentsProps={{
            tooltip: {
              sx: {
                fontSize: '1rem',
                padding: '8px 12px'
              }
            }
          }}
        >
          {inputField}
        </Tooltip>
      ) : inputField}
      <Menu {...menuProps}>
        {props.value.getOptions().map(val => (
          <MenuItem
            onClick={() => {
              setValue(val);
              eValue.value = val as T;
              inputRef.current?.blur();
              handleClose();
            }}
            key={String(val)}
          >
            {getDisplay ? getDisplay(val) : String(val)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export const SelectEditor = observer(SelectEditorCore);
